import { LIMITS } from "../entitlements/catalog";
import { TAP_RATE_FIXED_CENTS, TAP_RATE_PERCENT } from "./pricing";
import type { PaymentPlan } from "./types";

export const UserModuleErrorCodes = {
  invalidOtp: "invalidOtp",
  emailExists: "emailExists",
  emailExistsWithOAuth: "emailExistsWithOAuth",
  emailExistsWithPassword: "emailExistsWithPassword",
  userNotVerified: "userNotVerified",
  resetRequired: "resetRequired",
};

export const Timezones = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
];

export const defaultTaxSettings = {
  enableTax: false,
  taxRate: 0,
  taxName: "",
  showTaxIdOnReceipt: false,
  pricesInclusiveOfTax: false,
  taxId: "",
};

export const defaultTipSettings = {
  enableTip: false,
  enableTipSkip: true,
  predefinedTipPercentages: [10, 15, 20],
  isInFixedAmount: false,
};

/**
 * The Freepay settings written to a profile that has none yet.
 *
 * `creditRate`/`creditFlat` are read only by older app versions; current
 * versions charge the platform Freepay rate (`FREEPAY_RATE_BPS`) and ignore
 * them. Older versions set no merchant fee, so the Stripe pricing tool charges
 * the merchant the tap rate. The stored rate must therefore stay at the tap
 * rate, so an older version never charges a customer more than the merchant
 * pays.
 */
export const defaultFreepaySettings = {
  enabled: false,
  attestedAt: null,
  creditRate: TAP_RATE_PERCENT,
  creditFlat: TAP_RATE_FIXED_CENTS / 100,
};

// For more field names refer to `amplify/data/merchant/Business.schema.md`.
// For more details refer to `https://docs.stripe.com/connect/required-verification-information`.
export const StripeBusinessErrorFieldNames = {
  externalAccount: "external_account",
  businessUrl: "business_profile.url",
  taxId: "company.tax_id",
  individualIdNumber: "individual.id_number",
  tosAcceptanceDate: "tos_acceptance.date",
  tosAcceptanceIp: "tos_acceptance.ip",
  companyPhone: "company.phone",
  mcc: "business_profile.mcc",
};

export const StripeBusinessErrorFieldRegexes = {
  personRegex: /^person_[^.]+\..*/, // matches person_123.*
  personIdRegex: /^person_[^.]+\.id_number/, // matches person_123.id_number
  personVerificationDocumentRegex: /^person_[^.]+\.verification\.document/, // matches person_123.verification.document
  individualCompanyAddressRegex: /^(company|individual)\.address\./,
  personAddressRegex: /^person_[^.]+\.address\./,
  personProofOfLivenessRegex: /^person_[^.]+\.verification\.proof_of_liveness/, // matches person_123.verification.document
  ownersFieldRegex: /^owners\./,
  directorsFieldRegex: /^directors\./,
  companyProvisionFlagRegex:
    /^company\.(owners|directors|executives)_provided$/,
};

export const SETUP_GUIDE_HANDLED_REQUIREMENTS = new Set([
  "individual.id_number",
  "company.tax_id",
  "company.registration_number",
  "company.phone",
  "individual.verification.document",
  "tos_acceptance.date",
  "tos_acceptance.ip",
  "documents.proof_of_registration.files",
  "company.verification.document",
  "external_account",
  "business_profile.url",
  "business_profile.mcc",
  "individual.verification.proof_of_liveness",
]);

// Derived from the entitlement catalog rather than restated, so the plan
// ceilings have one definition. These names predate the catalog and stay because
// a dozen call sites use them.
export const defaultChargeLimitCents =
  LIMITS["payments.transaction_max"].byPlan.free; // $1,000.00
export const maxChargeLimitCents =
  LIMITS["payments.transaction_max"].byPlan.pro; // $10,000.00

export const BusinessStripeCapabilities = {
  charges: "charges",
  payouts: "payouts",
};

export const freePlan: PaymentPlan = {
  planName: "free",
  transactionLimit: defaultChargeLimitCents,
  planExpiresAt: null,
  isInvoluntaryDowngrade: false,
};

export const paidPlan: PaymentPlan = {
  planName: "pro",
  transactionLimit: maxChargeLimitCents,
  planExpiresAt: null,
  isInvoluntaryDowngrade: false,
};
